"use client";

import { CopyButton, Notice } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import type { OrcidStrings } from "@/components/tools/orcid-publication-list";
import { Badge } from "@/components/tools/reference-tools/badge";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import type { SavedCitation } from "@/lib/bibliography";
import type { EditableEntry } from "@/lib/citation";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { cslToBibtex } from "@/lib/tool-kit/reference-tools/bibtex-write";
import type { OrcidWork } from "@/lib/tool-kit/reference-tools/orcid";
import type {
  OrcidProfile,
  WorkSource,
} from "@/lib/tool-kit/reference-tools/orcid-run";

/*
 * The ORCID Publication List Generator's two panels: the works on the record, to pick
 * from, and the formatted list with its exports. Every word comes from the widget's
 * strings.
 */

/** The site's exporters, loaded on demand: they bring the whole formatter with them. */
export interface Exporters {
  exportAsText: (citations: SavedCitation[], numbered?: boolean) => string;
  exportAsHtml: (citations: SavedCitation[]) => string;
  exportAsRis: (citations: SavedCitation[]) => string;
  editableEntryToCsl: (entry: EditableEntry) => Record<string, unknown>;
}

export interface FormattedWork {
  work: OrcidWork;
  source: WorkSource;
  citation: string | null;
  style: string;
}

const BIB_MIME = "application/x-bibtex;charset=utf-8";
const RIS_MIME = "application/x-research-info-systems;charset=utf-8";
const LINK = "break-all text-primary underline-offset-4 hover:underline";

function typeLabel(type: string, strings: OrcidStrings): string {
  return (strings.types as Record<string, string>)[type] ?? type;
}

export function WorksTable({
  profile,
  selected,
  onSelect,
  max,
  strings,
}: {
  profile: OrcidProfile;
  selected: ReadonlySet<number>;
  onSelect: (next: Set<number>) => void;
  max: number;
  strings: OrcidStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const count = profile.works.length;

  function toggle(putCode: number, include: boolean) {
    const next = new Set(selected);
    if (include && next.size < max) {
      next.add(putCode);
    } else if (!include) {
      next.delete(putCode);
    }
    onSelect(next);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className={WIDGET_H2}>
          {profile.owner
            ? t(strings.worksHeading, { owner: profile.owner })
            : strings.worksHeadingAnonymous}
        </h2>
        <p className="text-muted-foreground text-sm">
          {tPlural(strings.worksCount, locale, count, { count: fmt(count) })}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            onSelect(
              new Set(profile.works.slice(0, max).map((work) => work.putCode))
            )
          }
          type="button"
        >
          {strings.selectAll}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => onSelect(new Set())}
          type="button"
        >
          {strings.selectNone}
        </button>
        <span className="text-muted-foreground text-sm">
          {t(strings.selected, { selected: fmt(selected.size), max: fmt(max) })}
        </span>
      </div>
      <div className="max-h-[28rem] overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.columns.include}
              </th>
              <th className={TH} scope="col">
                {strings.columns.year}
              </th>
              <th className={TH} scope="col">
                {strings.columns.type}
              </th>
              <th className={TH} scope="col">
                {strings.columns.title}
              </th>
              <th className={TH} scope="col">
                {strings.columns.doi}
              </th>
            </tr>
          </thead>
          <tbody>
            {profile.works.map((work) => (
              <tr key={work.putCode}>
                <td className={TD}>
                  <input
                    aria-label={work.title || strings.columns.include}
                    checked={selected.has(work.putCode)}
                    className="accent-primary"
                    onChange={(event) =>
                      toggle(work.putCode, event.target.checked)
                    }
                    type="checkbox"
                  />
                </td>
                <td className={`${TD} whitespace-nowrap`}>
                  {work.year === null ? strings.noDate : work.year}
                </td>
                <td className={`${TD} text-muted-foreground text-xs`}>
                  {typeLabel(work.type, strings)}
                </td>
                <td className={`${TD} min-w-56`}>
                  <span className="text-foreground">{work.title}</span>
                  {work.journal ? (
                    <span className="block text-muted-foreground text-xs">
                      {work.journal}
                    </span>
                  ) : null}
                </td>
                <td className={`${TD} font-mono text-xs`}>
                  {work.doi ? (
                    <a
                      className={LINK}
                      href={`https://doi.org/${work.doi}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {work.doi}
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function savedCitations(results: readonly FormattedWork[]): SavedCitation[] {
  return results.flatMap((result) =>
    result.citation
      ? [
          {
            id: String(result.work.putCode),
            citation: result.citation,
            entry: result.source.entry,
            style: result.style,
            savedAt: 0,
          },
        ]
      : []
  );
}

function ExportButtons({
  results,
  numbered,
  exporters,
  strings,
}: {
  results: readonly FormattedWork[];
  numbered: boolean;
  exporters: Exporters;
  strings: OrcidStrings;
}) {
  const saved = savedCitations(results);
  const text = exporters.exportAsText(saved, numbered);
  const html = exporters.exportAsHtml(saved);
  return (
    <div className="flex flex-wrap gap-3">
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyText}
        text={text}
      />
      <CopyButton
        copiedLabel={strings.copied}
        html={html}
        label={strings.copyHtml}
        text={text}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          downloadText(
            "publications.bib",
            cslToBibtex(
              saved.map((item) => exporters.editableEntryToCsl(item.entry))
            ),
            BIB_MIME
          )
        }
        type="button"
      >
        {strings.downloadBib}
      </button>
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          downloadText(
            "publications.ris",
            exporters.exportAsRis(saved),
            RIS_MIME
          )
        }
        type="button"
      >
        {strings.downloadRis}
      </button>
    </div>
  );
}

export function PublicationList({
  results,
  numbered,
  owner,
  exporters,
  exportsFailed,
  strings,
}: {
  results: readonly FormattedWork[];
  numbered: boolean;
  owner: string;
  exporters: Exporters | null;
  exportsFailed: boolean;
  strings: OrcidStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const formatted = results.filter((result) => result.citation).length;
  const styleName =
    (strings.styles as Record<string, string>)[results[0]?.style ?? ""] ?? "";
  let exportArea = <Notice>{strings.loadingExports}</Notice>;
  if (exportsFailed) {
    exportArea = <Notice>{strings.exportsFailed}</Notice>;
  } else if (exporters) {
    exportArea = (
      <ExportButtons
        exporters={exporters}
        numbered={numbered}
        results={results}
        strings={strings}
      />
    );
  }
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <div>
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <p className="text-muted-foreground text-sm">
          {tPlural(strings.formattedCount, locale, formatted, {
            count: fmt(formatted),
            style: styleName,
          })}
        </p>
      </div>
      {exportArea}
      <ol className={`space-y-3 ${numbered ? "list-decimal pl-6" : ""}`}>
        {results.map((result) => (
          <li
            className="space-y-1 text-foreground text-sm"
            key={result.work.putCode}
          >
            <p className="break-words pl-8 -indent-8">
              {result.citation ?? strings.notFormatted}
            </p>
            <Badge tone="neutral">
              {result.source.from === "doi"
                ? strings.fromDoi
                : strings.fromOrcid}
            </Badge>
            {result.source.ownerOnly ? (
              <p className="text-amber-800 text-xs dark:text-amber-300">
                {t(strings.ownerOnly, { owner })}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
      <p className={HINT}>{strings.italicsNote}</p>
    </div>
  );
}
